import React, { memo, useState } from "react";
import { 
  CheckSquare, 
  Square, 
  Trash2, 
  MessageSquare, 
  Send, 
  CheckCircle, 
  RotateCcw,
  X,
  AlertTriangle
} from "lucide-react";
import { stockProductionService } from "../../services/stockProductionService";

const hasBomRecipe = (sku, bomSkuSet) => {
  if (!sku || !bomSkuSet) return false;
  const cleanSku = sku.trim().toUpperCase();
  if (bomSkuSet.has(cleanSku)) return true;
  
  const parts = cleanSku.split("-");
  if (parts.length > 1) {
    for (let i = parts.length - 1; i >= 1; i--) {
      const parentSku = parts.slice(0, i).join("-");
      if (bomSkuSet.has(parentSku)) return true;
    }
  }
  return false;
};

const ALL_DEFAULT_STATUSES = [
  { id: "pe_solicitado", label: "Solicitado" },
  { id: "pe_imprimindo", label: "Imprimindo" },
  { id: "pe_cura", label: "Cura" },
  { id: "pe_fundicao", label: "Fundição" },
  { id: "pe_fundidos", label: "Fundidos" },
  { id: "pe_pre_escapulario", label: "Pré-Escapulário" },
  { id: "pe_acabamento", label: "Acab. externo" },
  { id: "pe_cravacao", label: "Cravação" },
  { id: "pe_polimento", label: "Polimento" },
  { id: "pe_revisao", label: "Revisão" },
  { id: "pe_finalizacao", label: "Finalização" },
  { id: "pe_conferencia", label: "Conferência" }
];

const ItemCard = memo(({ 
  item, 
  catalogItem, 
  isSelected, 
  currentConfig, 
  onToggleSelect, 
  onDelete,
  onSuspend,
  onReactivate,
  isLoteCancelado,
  onConvertLoteToAvulso,
  bomSkuSet,
  user,
  onAdvanceStatus,
  isOficina,
  availableStatuses
}) => {
  const [isMuralOpen, setIsMuralOpen] = useState(false);
  const [newMessageText, setNewMessageText] = useState("");
  const [isSending, setIsSending] = useState(false);

  const history = Array.isArray(item.history) ? item.history : [];
  const comments = history.filter(h => h.action === "comentario" || h.type === "user");
  const totalComments = comments.length;

  const handleSendMessage = async () => {
    const text = newMessageText.trim();
    if (!text || isSending) return;

    setIsSending(true);
    try {
      const authorName = user?.name || "Usuário";
      await stockProductionService.addComment(item.id, text, authorName);
      setNewMessageText("");
    } catch (e) {
      console.error("Erro ao enviar mensagem para o mural", e);
      alert("Erro ao enviar mensagem: " + e.message);
    } finally {
      setIsSending(false);
    }
  };

  const isBom = hasBomRecipe(item.sku, bomSkuSet);

  // Lista dinâmica de status (Padrões + Customizados do usuário)
  const baseList = availableStatuses && availableStatuses.length > 0 ? availableStatuses : ALL_DEFAULT_STATUSES;
  const hasCurrentStatus = baseList.some(s => s.id === item.status);
  const statusOptions = hasCurrentStatus 
    ? baseList 
    : [...baseList, { id: item.status, label: item.status }];

  const currentLabel = statusOptions.find(s => s.id === item.status)?.label || item.status;

  return (
    <div
      className={`bg-white border rounded-lg flex flex-col shadow-sm hover:shadow-md transition-all cursor-pointer ${
        isSelected
          ? "border-purple-400 bg-purple-50 ring-1 ring-purple-400"
          : "border-slate-200"
      } ${
        item.situacao_fluxo === "suspenso" 
          ? "opacity-60 border-l-red-500 bg-slate-50 border-l-4" 
          : `${currentConfig?.color || "border-l-blue-500"} border-l-4`
      }`}
      onClick={() => item.situacao_fluxo !== "suspenso" && onToggleSelect(item.id)}
    >
      {/* Principal Row Container */}
      <div className="p-3 flex flex-col md:flex-row md:items-center justify-between w-full">
        <div className="flex items-start gap-3 mb-2 md:mb-0 flex-1">
          <div
            className="mt-1 text-slate-400"
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelect(item.id);
            }}
          >
            {isSelected ? (
              <CheckSquare size={18} className="text-purple-600" />
            ) : (
              <Square size={18} />
            )}
          </div>

          <div>
            <div className="flex items-center gap-2">
              {/* Tag EP visible mobile */}
              <div className="md:hidden">
                <span
                  className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${currentConfig?.bg || "bg-blue-50"} ${currentConfig?.text || "text-blue-700"}`}
                >
                  EP
                </span>
              </div>
              <span className="font-bold text-blue-600 text-sm">{item.sku}</span>
              
              {/* Badge INTERCEPTADA */}
              {(item.situacao_fluxo === "interceptado" || item.status === "pe_interceptado") && (
                <span className="bg-amber-500 text-white text-[9px] font-black px-2 py-0.5 rounded flex items-center gap-1 shadow-2xs" title={`Interceptada para o pedido ${item.pedido_interceptador || item.pedido_id || "Venda"}`}>
                  ⚡ INTERCEPTADA {item.pedido_interceptador || item.pedido_id ? `(#${item.pedido_interceptador || item.pedido_id})` : ""}
                </span>
              )}

              {/* Badge SUSPENSA */}
              {item.situacao_fluxo === "suspenso" && (
                <span className="bg-red-650 text-white text-[9px] font-black px-2 py-0.5 rounded flex items-center gap-1 shadow-2xs">
                  🚫 PEÇA SUSPENSA
                </span>
              )}

              {/* Tag BOM */}
              {isBom && (
                <span className="bg-amber-100 text-amber-800 text-[9px] font-black px-1.5 py-0.5 rounded border border-amber-200 flex items-center gap-0.5">
                  💎 BOM
                </span>
              )}

              {/* Tag Inativo na loja */}
              {catalogItem?.isInactive && (
                <span className="bg-slate-100 text-slate-500 text-[9px] font-bold px-1.5 py-0.5 rounded border border-slate-200" title="Produto inativo na loja virtual, mas disponível para a fábrica">
                  Inativo na loja
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500 truncate max-w-md">
              {item.name || catalogItem?.name || (catalogItem?.sku ? `Produto Base: ${catalogItem.sku}` : "Produto sem cadastro")}
            </p>

            {/* Contador de Passagens por Status (S1.4) */}
            {(() => {
              const counts = {};
              history.forEach(evt => {
                if (evt.to) {
                  counts[evt.to] = (counts[evt.to] || 0) + 1;
                }
              });
              const entries = Object.entries(counts);
              if (entries.length === 0) return null;
              return (
                <div className="flex flex-wrap gap-1 mt-1">
                  {entries.map(([stId, count]) => {
                    const label = statusOptions.find(s => s.id === stId)?.label || stId;
                    return (
                      <span key={stId} className="text-[9px] font-bold text-slate-500 bg-slate-100 px-1.5 py-0.2 rounded border border-slate-200">
                        Passou {count}× por {label}
                      </span>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>

        {/* Direita: Ações, Data e Status */}
        <div
          className="flex flex-wrap items-center justify-between md:justify-end gap-3 pl-8 md:pl-0 mt-2 md:mt-0"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-right shrink-0">
            <span className="text-[10px] text-slate-400 block md:hidden">
              Criado em
            </span>
            <span className="text-xs font-bold text-slate-600">
              {(() => {
                const val = item.dateIn || item.created;
                if (!val) return "Hoje";
                try {
                  const d = new Date(val);
                  if (!isNaN(d.getTime())) return d.toLocaleDateString("pt-BR");
                } catch (e) {}
                return val;
              })()}
            </span>
          </div>

          {/* Seletor de Status Flexível Dinâmico (Todos os Status + Customizados) */}
          <select
            className="text-[10px] font-extrabold px-2.5 py-1 rounded-lg border border-purple-200 bg-purple-50 text-purple-800 hover:bg-purple-100 outline-none cursor-pointer transition-all shadow-sm"
            value={item.status}
            onChange={(e) => {
              const targetStatus = e.target.value;
              if (targetStatus && targetStatus !== item.status) {
                onAdvanceStatus(item.id, targetStatus);
              }
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {statusOptions.map((st) => (
              <option key={st.id} value={st.id} className="bg-white text-slate-800 font-medium">
                {st.label}
              </option>
            ))}
            {!isOficina && (
              <option value="in_stock" className="bg-white text-emerald-700 font-bold">
                ✓ Concluir em Estoque
              </option>
            )}
          </select>

          {/* Toggle do Mural, Lixeira e Suspensão */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsMuralOpen(!isMuralOpen)}
              className={`p-1.5 rounded-lg transition-all flex items-center gap-0.5 ${
                isMuralOpen 
                  ? "text-purple-600 bg-purple-50 hover:bg-purple-100" 
                  : totalComments > 0 
                    ? "text-purple-600 bg-purple-50/50 hover:bg-purple-50"
                    : "text-slate-400 hover:text-purple-600 hover:bg-purple-50"
              }`}
              title="Mural de Mensagens e Histórico"
            >
              <MessageSquare size={15} />
              {totalComments > 0 && (
                <span className="text-[9px] font-bold px-1 rounded-full bg-purple-100 text-purple-700 min-w-[14px] text-center">
                  {totalComments}
                </span>
              )}
            </button>

            {item.status !== "pe_conferencia" && (
              item.situacao_fluxo === "suspenso" ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onReactivate) onReactivate(item.id);
                  }}
                  className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                  title="Reativar/Retornar Peça para Produção"
                >
                  <RotateCcw size={15} />
                </button>
              ) : item.lote ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSuspend(item.id);
                  }}
                  className="p-1.5 text-slate-400 hover:text-red-650 hover:bg-red-50 rounded-lg transition-colors"
                  title="Suspender Peça"
                >
                  <X size={15} />
                </button>
              ) : (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(item.id);
                  }}
                  className="p-1.5 text-slate-400 hover:text-red-650 hover:bg-red-50 rounded-lg transition-colors"
                  title="Remover Item"
                >
                  <Trash2 size={15} />
                </button>
              )
            )}
          </div>
        </div>
      </div>

      {/* SEÇÃO DO MURAL ( timeline / histórico ) */}
      {isMuralOpen && (
        <div 
          className="border-t border-slate-100 bg-slate-50/70 px-4 py-3 flex flex-col gap-3.5 animate-in slide-in-from-top duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center">
            <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
              <MessageSquare size={13} className="text-purple-500" /> Mural de Mensagens &amp; Histórico
            </h4>
            <span className="text-[9px] text-slate-400 font-medium">
              {history.length} registro{history.length !== 1 ? "s" : ""}
            </span>
          </div>

          {/* LISTA DE EVENTOS E MENSAGENS */}
          <div className="flex flex-col gap-2 max-h-48 overflow-y-auto pr-1 custom-scrollbar">
            {history.length === 0 ? (
              <div className="text-center py-4 text-slate-400 text-xs italic">
                Nenhum registro no histórico deste lote.
              </div>
            ) : (
              history.map((msg, idx) => {
                const isSystem = msg.type === "system" || msg.action !== "comentario";
                const dateStr = msg.date ? new Date(msg.date).toLocaleString("pt-BR", {
                  day: "2-digit",
                  month: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit"
                }) : "";

                if (isSystem) {
                  return (
                    <div key={msg.id || idx} className="flex justify-center my-0.5">
                      <div className="bg-slate-100 border border-slate-200 text-slate-600 rounded px-2.5 py-1 text-[10px] max-w-full text-center flex items-center gap-1.5 leading-relaxed font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                        <span>{msg.text || `${msg.action} (${msg.from} -> ${msg.to})`}</span>
                        <span className="text-[9px] text-slate-400 font-mono ml-1">{dateStr}</span>
                      </div>
                    </div>
                  );
                }

                // Bolha de comentário do usuário
                const isCurrentUser = user && msg.user && (msg.user.toLowerCase() === user.name?.toLowerCase());
                return (
                  <div key={msg.id || idx} className={`flex flex-col gap-0.5 max-w-[85%] ${isCurrentUser ? "self-end items-end" : "self-start items-start"}`}>
                    <div className="flex items-center gap-1.5 text-[9px] font-bold text-slate-500 px-1">
                      <span>{msg.user || "Usuário"}</span>
                      <span className="text-[8px] font-normal text-slate-400 font-mono">{dateStr}</span>
                    </div>
                    <div className={`rounded-xl px-2.5 py-1.5 text-xs shadow-sm leading-relaxed ${
                      isCurrentUser 
                        ? "bg-purple-600 text-white rounded-tr-none" 
                        : "bg-white border border-slate-200 text-slate-700 rounded-tl-none"
                    }`}>
                      {msg.text}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* INPUT PARA ENVIAR MENSAGEM */}
          <div className="flex gap-2 items-center border-t border-slate-200 pt-2.5">
            <input
              type="text"
              placeholder="Escreva uma observação..."
              className="flex-1 bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:border-purple-500 shadow-inner text-slate-800"
              value={newMessageText}
              onChange={(e) => setNewMessageText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              disabled={isSending}
            />
            <button
              onClick={handleSendMessage}
              disabled={isSending || !newMessageText.trim()}
              className="bg-purple-600 hover:bg-purple-700 text-white rounded-lg p-1.5 transition-all shadow-sm hover:shadow disabled:bg-purple-400 disabled:cursor-not-allowed cursor-pointer shrink-0 flex items-center justify-center"
              title="Enviar observação"
            >
              <Send size={12} />
            </button>
          </div>
        </div>
      )}

      {isLoteCancelado && (
        <div className="bg-amber-50 border-t border-amber-200 px-4 py-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs font-bold text-amber-800 shrink-0">
          <div className="flex items-center gap-2">
            <AlertTriangle size={15} className="text-amber-600 animate-pulse" />
            <span>O Lote original desta peça foi <strong>SUSPENSO ou CANCELADO</strong>!</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onConvertLoteToAvulso) onConvertLoteToAvulso(item.id);
              }}
              className="px-3 py-1 bg-amber-600 hover:bg-amber-755 text-white rounded-lg text-[10px] font-black transition-all shadow-xs"
              title="Remove o lote e mantém a peça na produção de forma avulsa"
            >
              Prosseguir como Avulso
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onDelete) onDelete(item.id);
              }}
              className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg text-[10px] font-black transition-all shadow-xs"
              title="Exclui fisicamente a peça do fluxo de fábrica"
            >
              Eliminar do Fluxo
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

export default ItemCard;
